import useUploadPortalBackgroundImage from "@/api/portal-background-image.ts";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";
import { Form, FormField } from "@babackapp/ui/components/form";
import FormColorInput from "@babackapp/react-hook-forms-components/form-color-input";
import FormEditableAvatar from "@babackapp/react-hook-forms-components/form-editable-avatar";
import FormInputRow from "@babackapp/react-hook-forms-components/form-input-row";
import { SettingsFormPageButtonBar } from "@babackapp/react-hook-forms-components/form-page";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import useTranslation from "@babackapp/translations/use-translation";
import { z } from "zod";

const FormCustomizationSchema = z.object({
  primary50: z.string().min(7).max(7),
  primary200: z.string().min(7).max(7),
  primary400: z.string().min(7).max(7),
  primary600: z.string().min(7).max(7),
  primary800: z.string().min(7).max(7),
});

export type FormCustomizationValues = z.infer<typeof FormCustomizationSchema>;

type FormCustomizationProps = {
  onSubmit: (data: FormCustomizationValues) => void;
  defaultValues: FormCustomizationValues;
};

export default function ShopCustomizationForm({
  onSubmit,
  defaultValues,
}: FormCustomizationProps) {
  const t = useTranslation();

  const {
    mutateAsync: uploadPortalBackground,
    isPending: uploadBackgroundLoading,
  } = useUploadPortalBackgroundImage();

  const { data: shop } = useCurrentShopSuspense();

  const form = useForm<FormCustomizationValues>({
    resolver: zodResolver(FormCustomizationSchema),
    defaultValues: defaultValues,
  });

  if (!shop) return null;

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col justify-start gap-10 mt-8"
      >
        <FormInputRow>
          <FormField
            control={form.control}
            name="primary50"
            render={({ field }) => (
              <FormColorInput
                label={t(
                  "settings.customerPortal.customization.fields.primary50.title",
                )}
                description={t(
                  "settings.customerPortal.customization.fields.primary50.description",
                )}
                {...field}
              />
            )}
          />
          <FormField
            control={form.control}
            name="primary200"
            render={({ field }) => (
              <FormColorInput
                label={t(
                  "settings.customerPortal.customization.fields.primary200.title",
                )}
                description={t(
                  "settings.customerPortal.customization.fields.primary200.description",
                )}
                {...field}
              />
            )}
          />
        </FormInputRow>

        <FormInputRow>
          <FormField
            control={form.control}
            name="primary400"
            render={({ field }) => (
              <FormColorInput
                label={t(
                  "settings.customerPortal.customization.fields.primary400.title",
                )}
                description={t(
                  "settings.customerPortal.customization.fields.primary400.description",
                )}
                {...field}
              />
            )}
          />
          <FormField
            control={form.control}
            name="primary600"
            render={({ field }) => (
              <FormColorInput
                label={t(
                  "settings.customerPortal.customization.fields.primary600.title",
                )}
                description={t(
                  "settings.customerPortal.customization.fields.primary600.description",
                )}
                {...field}
              />
            )}
          />
        </FormInputRow>

        <FormInputRow>
          <FormField
            control={form.control}
            name="primary800"
            render={({ field }) => (
              <FormColorInput
                label={t(
                  "settings.customerPortal.customization.fields.primary800.title",
                )}
                description={t(
                  "settings.customerPortal.customization.fields.primary800.description",
                )}
                {...field}
              />
            )}
          />
          <FormEditableAvatar
            label={t(
              "settings.customerPortal.customization.fields.backgroundImage.title",
            )}
            description={t(
              "settings.customerPortal.customization.fields.backgroundImage.description",
            )}
            fallback={shop.name}
            initialValue={shop.preferences.userPortal.backgroundImageUrl}
            onRemove={() => {}}
            isLoading={uploadBackgroundLoading}
            onUpload={uploadPortalBackground}
          />
        </FormInputRow>

        <SettingsFormPageButtonBar />
      </form>
    </Form>
  );
}
